import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useEffect, useState, useContext } from 'react';
import { Select } from '@bingads-webui-react/primitive';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withValidation, withError, withServerError } from '@bingads-webui-react/validation';
import { Input } from '@bingads-webui-campaign-react/fluent-with-validation';

export const AccountPhoneNumberRegex = /^\+?[\d\-\s()extX.]*$/;
export const PhoneNumberRegex = /^(\+0?1\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/;
export const OptionalPhoneNumberRegex = /^((\+0?1\s)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4})?$/;

export const defaultSchema = [
  {
    key: 'US',
    label: _TL_('United States'),
    countryCode: '+1',
    formats: [
      '(###) ###-####',
    ],
  },
  {
    key: 'CN',
    label: _TL_('China'),
    countryCode: '+86',
    formats: [
      '### #### ####',
    ],
  },
  {
    key: 'UK',
    label: _TL_('United Kingdom'),
    countryCode: '+44',
    formats: [
      '###-###-####',
    ],
    disabled: true,
  },
];

// Only support US phone number for campaign
export const usPhoneSchema = _.where(defaultSchema, { key: 'US' });

export const getPhoneNumberError = (errors, i18n) => {
  if (errors && errors.required) {
    return [i18n.getString(_TL_('Phone number is required.'))];
  }
  if (errors && errors.maxLength) {
    return [i18n.getString(_TL_('Phone number exceeds the maximum allowed length.'))];
  }
  if (errors && errors.pattern) {
    return [i18n.getString(_TL_('Enter a valid phone number.'))];
  }
  return _.values(errors);
};

export const getRegex = (format = '') => {
  const encode = (char) => {
    switch (char) {
      case '#':
        return '\\d';
      case '(':
      case ')':
        return `\\${char}`;
      default:
        return char;
    }
  };

  const str = ['^', ..._.map(format.split(''), encode), '$'].join('');
  const regex = new RegExp(str);

  return regex;
};

export const formatPhoneNumber = (input = '', format = '') => {
  let i = 0;
  let j = 0;

  const m = input.length;
  const n = format.length;
  const chars = [];

  while (i < m && j < n) {
    if (input[i] === format[j]) {
      chars.push(input[i]);
      i += 1;
      j += 1;
    } else if (input[i] < '0' || input[i] > '9') {
      i += 1;
    } else if (format[j] === '#') {
      chars.push(input[i]);
      i += 1;
      j += 1;
    } else {
      chars.push(format[j]);
      j += 1;
    }
  }

  while (i < m) {
    chars.push(input[i]);
    i += 1;
  }

  return chars.join('');
};

export const repairInput = input => (
  (input || '').toString().replace(/\+?([^\n]*)/, '+$1')
);

export const PhoneNumberPure = (props) => {
  const {
    id,
    className,
    schema,
    onChange,
    value: valueInProps,
  } = props;

  const [selectedCountryCode, setSelectedCountryCode] = useState(undefined);
  const [onChangeMetric, setOnChangeMetric] = useState({});
  const i18n = useContext(I18nContext);

  const value = useMemo(() => repairInput(valueInProps), [valueInProps]);

  const enabledSchema = useMemo(() => (
    _.filter(schema || defaultSchema, s => s.disabled !== true)
  ), [schema]);

  const selectedSchema = useMemo(() => (
    _.find(
      enabledSchema,
      s => value.indexOf(s.countryCode) === 0 || selectedCountryCode === s.countryCode
    ) || defaultSchema[0]
  ), [enabledSchema, selectedCountryCode, value]);

  const countryCode = useMemo(() => (
    selectedSchema.countryCode
  ), [selectedSchema]);

  const selectedFormat = useMemo(() => {
    if (value.indexOf(countryCode) !== 0) {
      return selectedSchema.formats[0];
    }

    return _.find(
      selectedSchema.formats,
      f => getRegex(f).test(value.substr(countryCode.length).trim())
    ) || selectedSchema.formats[0];
  }, [countryCode, selectedSchema.formats, value]);

  const phoneNumber = useMemo(() => formatPhoneNumber(
    value.indexOf(countryCode) !== 0 ? value.replace(/\D/g, '') : value.substr(countryCode.length),
    selectedFormat
  ), [countryCode, selectedFormat, value]);

  useEffect(() => {
    if (onChangeMetric.cursor === 0 && onChangeMetric.beforeCursor === '') {
      onChangeMetric.event.target.setSelectionRange(0, 0);
    } else if (onChangeMetric.cursor && onChangeMetric.event && onChangeMetric.beforeCursor) {
      const diff = phoneNumber.indexOf(onChangeMetric.beforeCursor);
      if (diff !== -1) {
        onChangeMetric.event.target.setSelectionRange(
          diff + onChangeMetric.cursor,
          diff + onChangeMetric.cursor
        );
      }
      setOnChangeMetric({});
    }
  }, [phoneNumber, onChangeMetric]);

  const handleChange = useCallback((code, number) => {
    const newValue = _.isEmpty(number) ? '' : `${code} ${number}`;
    if (valueInProps !== newValue) {
      onChange({ target: { value: newValue } });
    }
  }, [valueInProps, onChange]);

  const handleCountryCodeChange = useCallback(({
    target: { value: code },
  }) => {
    setSelectedCountryCode(code);
    handleChange(code, phoneNumber);
  }, [phoneNumber, handleChange]);

  const handlePhoneNumberChange = useCallback((event) => {
    event.persist();
    setOnChangeMetric({
      event,
      cursor: event.target.selectionStart,
      beforeCursor: event.target.value.substr(0, event.target.selectionStart),
    });
    handleChange(countryCode, formatPhoneNumber(event.target.value, selectedFormat));
  }, [countryCode, selectedFormat, handleChange]);

  useEffect(() => {
    if ((!_.isEmpty(phoneNumber) && value !== `${countryCode} ${formatPhoneNumber(phoneNumber)}`)
      || (_.isEmpty(phoneNumber) && value !== '')) {
      handleChange(countryCode, formatPhoneNumber(phoneNumber, selectedFormat));
    }
  }, [value, countryCode, phoneNumber, selectedFormat, handleChange]);

  return (
    <div style={{ display: 'flex' }} className="mca-phone-number">
      <Select
        id={id && `${id}-country-code`}
        className={classNames(className, 'select left')}
        value={countryCode}
        onChange={handleCountryCodeChange}
      >
        {_.map(enabledSchema, item => (
          <option key={item.key} value={item.countryCode}>
            {`${i18n.getString(item.label)} (${item.countryCode})`}
          </option>
        ))}
      </Select>
      <Input
        id={id && `${id}-phone-number`}
        className={classNames(className, 'text right')}
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        placeholder={selectedFormat}
        name={`${className}-PhoneNumber`}
        serverErrorProps={{ fieldSelector: 'PhoneNumber' }}
      />
    </div>
  );
};

PhoneNumberPure.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  schema: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    countryCode: PropTypes.string.isRequired,
    formats: PropTypes.arrayOf(PropTypes.string).isRequired,
  })),
};

PhoneNumberPure.defaultProps = {
  id: null,
  className: null,
  value: null,
  onChange: _.noop,
  schema: null,
};

export const PhoneNumber = withError(withValidation(PhoneNumberPure));
export const PhoneNumberWithServerError = withServerError(PhoneNumber);
